<template>
  <div>
    <!-- 载入动画 -->
    <LoadingAnimation 
      v-if="showLoading" 
      :duration="3000"
      @loading-complete="onLoadingComplete"
    />
    
    <!-- 主要内容 -->
    <div v-show="!showLoading" class="page-marg mt-0" :class="{ 'fade-in': contentVisible }">
      <!-- 个人状况仪表盘 -->
      <div 
        data-aos="fade-up" 
        data-aos-duration="800" 
        data-aos-delay="100"
        data-aos-anchor-placement="top-bottom"
        data-aos-offset="50"
      >
        <PersonalDashboard />
      </div>
      
      <!-- 技术成长时间轴 -->
      <div 
        data-aos="fade-up" 
        data-aos-duration="800" 
        data-aos-delay="200"
        data-aos-anchor-placement="top-bottom"
        data-aos-offset="50"
      >
        <TechTimeline />
      </div>
      
      <!-- 旅游足迹展示 -->
      <div 
        data-aos="fade-up" 
        data-aos-duration="800" 
        data-aos-delay="300"
        data-aos-anchor-placement="top-bottom"
        data-aos-offset="50"
      >
        <TravelShowcase />
      </div>
      
      <!-- 音乐和运动展示 -->
      <div 
        data-aos="fade-up" 
        data-aos-duration="800" 
        data-aos-delay="400"
        data-aos-anchor-placement="top-bottom"
        data-aos-offset="50"
      >
        <MusicSports />
      </div>
    </div>
  </div>
</template>

<script>
import AOS from 'aos'
import 'aos/dist/aos.css'
import LoadingAnimation from '@/components/LoadingAnimation.vue'
import TechTimeline from '@/components/TechTimeline.vue'
import TravelShowcase from '@/components/TravelShowcase.vue'
import PersonalDashboard from '@/components/PersonalDashboard.vue'
import MusicSports from '@/components/MusicSports.vue'

export default {
  name: 'HomeView',
  components: {
    LoadingAnimation,
    PersonalDashboard,
    TechTimeline,
    TravelShowcase,
    MusicSports
  },
  data() {
    return {
      showLoading: false,
      contentVisible: true,
      hasVisited: false
    }
  },
  mounted() {
    // 检查是否是第一次访问首页
    const hasVisitedHome = localStorage.getItem('hasVisitedHome')
    
    if (!hasVisitedHome) {
      // 第一次访问，显示载入动画
      this.showLoading = true
      this.contentVisible = false
      this.hasVisited = false
      // 标记已访问
      localStorage.setItem('hasVisitedHome', 'true')
    } else {
      // 非第一次访问，直接显示内容
      this.showLoading = false
      this.contentVisible = true
      this.hasVisited = true
    }
    
    // 初始化AOS动画库
    this.$nextTick(() => {
      if (typeof AOS !== 'undefined') {
        AOS.init({
          duration: 800,
          easing: 'ease-in-out',
          once: false, // 改为false，允许重复触发
          offset: 50, // 减少偏移量，更早触发动画
          delay: 0,
          anchorPlacement: 'top-bottom',
          disable: false,
          startEvent: 'DOMContentLoaded',
          useClassNames: false,
          disableMutationObserver: false,
          debounceDelay: 50,
          throttleDelay: 99,
          mirror: true // 开启镜像效果，支持双向滚动动画
        })
        
        // 如果不是第一次访问，立即刷新AOS动画
        if (this.hasVisited) {
          setTimeout(() => {
            AOS.refresh()
          }, 100)
        }
      }
    })
  },
  activated() {
    // Vue keep-alive 激活时重新初始化动画
    this.$nextTick(() => {
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    })
  },
  methods: {
    onLoadingComplete() {
      this.showLoading = false
      // 延迟一点显示内容，让过渡更自然
      setTimeout(() => {
        this.contentVisible = true
        // 等待DOM更新后重新初始化AOS动画
        this.$nextTick(() => {
          if (typeof AOS !== 'undefined') {
            AOS.refresh()
            // 确保滚动检测正常工作
            setTimeout(() => {
              AOS.refreshHard()
              // 手动触发一次滚动检测
              window.dispatchEvent(new Event('scroll'))
            }, 500)
          }
        })
      }, 200)
    }
  }
}
</script>

<style scoped>
.page-marg {
  padding: 2rem 0;
  min-height: 100vh;
  background: var(--bg-primary);
  transition: background-color 0.3s ease;
}

/* 确保组件之间有适当的间距 */
.page-marg > * {
  margin-bottom: 2rem;
}

.page-marg > *:last-child {
  margin-bottom: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-marg {
    padding: 1rem 0;
  }
  
  .page-marg > * {
    margin-bottom: 1.5rem;
  }
}

@media (max-width: 576px) {
  .page-marg {
    padding: 0.5rem 0;
  }
  
  .page-marg > * {
    margin-bottom: 1rem;
  }
}

/* 夜间模式适配 */
.dark-mode .page-marg {
  background: var(--bg-primary);
}

/* 滚动优化 */
.page-marg {
  scroll-behavior: smooth;
}

/* 为AOS动画添加基础样式 */
[data-aos] {
  pointer-events: none;
}

[data-aos].aos-animate {
  pointer-events: auto;
}

/* 自定义滚动条样式 */
.page-marg::-webkit-scrollbar {
  width: 8px;
}

.page-marg::-webkit-scrollbar-track {
  background: var(--bg-tertiary);
  border-radius: 4px;
}

.page-marg::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: all 0.3s ease;
}

.page-marg::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

/* 页面加载动画 */
.page-marg {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease-in-out;
}

.page-marg.fade-in {
  opacity: 1;
  transform: translateY(0);
}

/* AOS动画增强样式 */
[data-aos] {
  opacity: 0;
  transition-property: opacity, transform;
  pointer-events: none;
}

[data-aos].aos-animate {
  opacity: 1;
  pointer-events: auto;
}

/* 自定义AOS动画效果 */
[data-aos="fade-up"] {
  transform: translateY(50px);
  opacity: 0;
}

[data-aos="fade-up"].aos-animate {
  transform: translateY(0);
  opacity: 1;
}

[data-aos="slide-left"] {
  transform: translateX(-50px);
  opacity: 0;
}

[data-aos="slide-left"].aos-animate {
  transform: translateX(0);
  opacity: 1;
}

[data-aos="slide-right"] {
  transform: translateX(50px);
  opacity: 0;
}

[data-aos="slide-right"].aos-animate {
  transform: translateX(0);
  opacity: 1;
}

/* 双向滚动动画支持 */
[data-aos][data-aos-mirror="true"] {
  transition-duration: 0.8s;
  transition-timing-function: ease-in-out;
}

/* 确保动画在视窗外时重置 */
[data-aos]:not(.aos-animate) {
  visibility: hidden;
}

[data-aos].aos-animate {
  visibility: visible;
}

/* 组件间的分隔线效果 */
.page-marg > *:not(:last-child)::after {
  content: '';
  display: block;
  width: 100px;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--border-color), transparent);
  margin: 3rem auto 0;
}

@media (max-width: 768px) {
  .page-marg > *:not(:last-child)::after {
    margin: 2rem auto 0;
  }
}
</style>